/**
 * OaSIS Explorer Frontend - Occupation Code Prefix Component
 * 
 * Displays occupation code prefix in a formatted badge style
 * e.g., "# 0XXX.XX" for prefix "0", "# 00XX.XX" for prefix "00"
 */

import React from 'react';
import { Hash } from 'lucide-react';

interface OccupationCodePrefixProps {
  prefix: string;
  className?: string;
  level?: number; // Hierarchy level (1-7) to determine formatting
}

/**
 * Occupation code prefix badge component
 * Formats the prefix to show the pattern (e.g., "0XXX.XX" for prefix "0")
 * Level-based formatting:
 * - Level 5 (Minor group): 5 digits -> "XXXXX.X"
 * - Level 6 (NOC unit group): 5 digits -> "XXXXX.XX"
 */
export const OccupationCodePrefix: React.FC<OccupationCodePrefixProps> = ({
  prefix,
  className = '',
  level,
}) => {
  // Format prefix to always show 5 integers and 2 decimal places
  // "0" -> "0XXXX.XX"
  // "00" -> "00XXX.XX"
  // "000" -> "000XX.XX"
  // "0000" -> "0000X.XX"
  // "00000" -> "00000.XX"
  // For full codes (7+ digits), show the full code as-is
  const formatPrefix = (pref: string): string => {
    const length = pref.length;
    
    // If it's already a full OaSIS code (7+ digits with decimal), show as-is
    if (length >= 7 && pref.includes('.')) {
      return pref;
    }
    
    // Always format as 5 digits + 2 decimal places
    // Pad with X's to make 5 digits total
    const xCount = 5 - length;
    const xPadding = 'X'.repeat(Math.max(0, xCount));
    
    return `${pref}${xPadding}.XX`;
  };

  const formattedCode = formatPrefix(prefix);

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded align-middle ${className}`}>
      <Hash size={10} className="text-gray-500 dark:text-gray-400" />
      <span className="font-mono text-xs text-gray-700 dark:text-gray-300 leading-none">{formattedCode}</span>
    </span>
  );
};

